import { timeCapsuleApi } from '../services/apiService';
import { ZONE_IDS } from '../store/slices/zonesSlice';

/**
 * Utility functions for testing zone functionality
 */

// Test all zone endpoints
export const testAllZoneEndpoints = async () => {
    const testZoneId = ZONE_IDS.ORANG_LAUT;
    console.log('🧪 Starting comprehensive zone API tests...');

    try {
        // 1. Test GET zone status
        console.log('1️⃣ Testing GET zone status...');
        const initialStatus = await timeCapsuleApi.getZoneStatus(testZoneId);
        console.log('✅ Initial zone status:', initialStatus);

        // 2. Test PUT zone status (reset to available)
        console.log('2️⃣ Testing PUT zone status (reset to available)...');
        const resetStatus = await timeCapsuleApi.updateZoneStatus(testZoneId, {
            status: 'available',
            claimedBy: undefined,
        });
        console.log('✅ Zone reset to available:', resetStatus);

        // 3. Test POST claim zone
        console.log('3️⃣ Testing POST claim zone...');
        const claimedStatus = await timeCapsuleApi.claimZoneStatus(testZoneId);
        console.log('✅ Zone claimed:', claimedStatus);

        // 4. Test PUT zone payload
        console.log('4️⃣ Testing PUT zone payload...');
        const payloadStatus = await timeCapsuleApi.updateZonePayload(testZoneId, {
            payload: {
                additionalProp1: 'test-selection',
                additionalProp2: 'Test Item Title',
                additionalProp3: JSON.stringify({
                    timestamp: new Date().toISOString(),
                    testData: 'comprehensive API test',
                    status: 'completed',
                }),
            },
        });
        console.log('✅ Zone payload updated:', payloadStatus);

        // 5. Test final GET to verify changes
        console.log('5️⃣ Testing final GET to verify changes...');
        const finalStatus = await timeCapsuleApi.getZoneStatus(testZoneId);
        console.log('✅ Final zone status:', finalStatus);

        console.log('🎉 All zone API tests completed successfully!');
        return {
            success: true,
            results: {
                initialStatus,
                resetStatus,
                claimedStatus,
                payloadStatus,
                finalStatus,
            },
        };
    } catch (error) {
        console.error('❌ Zone API test failed:', error);
        return {
            success: false,
            error,
        };
    }
};

// Test zone claiming workflow
export const testZoneClaimingWorkflow = async () => {
    const testZoneId = ZONE_IDS.ORANG_LAUT;
    console.log('🔄 Testing zone claiming workflow...');

    try {
        // Reset zone to available
        await timeCapsuleApi.updateZoneStatus(testZoneId, {
            status: 'available',
            claimedBy: undefined,
        });

        // Wait a bit
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Claim the zone
        const claimedStatus = await timeCapsuleApi.claimZoneStatus(testZoneId);
        console.log('✅ Zone claiming workflow completed:', claimedStatus);

        return claimedStatus;
    } catch (error) {
        console.error('❌ Zone claiming workflow failed:', error);
        throw error;
    }
};

// Test MarketTraders zone specifically
export const testMarketTradersZone = async () => {
    console.log('🏪 Testing MarketTraders zone (4.2) specifically...');
    const testZoneId = ZONE_IDS.MARKET_TRADERS;
    
    try {
        // 1. Test GET zone status
        console.log('1️⃣ Testing GET zone status...');
        const initialStatus = await timeCapsuleApi.getZoneStatus(testZoneId);
        console.log('✅ Initial zone status:', initialStatus);

        // 2. Test PUT zone status (reset to available)
        console.log('2️⃣ Testing PUT zone status (reset to available)...');
        const resetStatus = await timeCapsuleApi.updateZoneStatus(testZoneId, {
            status: 'available',
            claimedBy: undefined,
        });
        console.log('✅ Zone reset to available:', resetStatus);

        // 3. Test POST claim zone
        console.log('3️⃣ Testing POST claim zone...');
        const claimedStatus = await timeCapsuleApi.claimZoneStatus(testZoneId);
        console.log('✅ Zone claimed:', claimedStatus);

        // 4. Test PUT zone payload
        console.log('4️⃣ Testing PUT zone payload...');
        const payloadStatus = await timeCapsuleApi.updateZonePayload(testZoneId, {
            payload: {
                additionalProp1: 'test-selection',
                additionalProp2: 'Market Traders Test',
                additionalProp3: JSON.stringify({
                    timestamp: new Date().toISOString(),
                    testData: 'MarketTraders comprehensive API test',
                    status: 'completed',
                }),
            },
        });
        console.log('✅ Zone payload updated:', payloadStatus);

        // 5. Test final GET to verify changes
        console.log('5️⃣ Testing final GET to verify changes...');
        const finalStatus = await timeCapsuleApi.getZoneStatus(testZoneId);
        console.log('✅ Final zone status:', finalStatus);

        console.log('🎉 MarketTraders zone API tests completed successfully!');
        return {
            success: true,
            results: {
                initialStatus,
                resetStatus,
                claimedStatus,
                payloadStatus,
                finalStatus,
            },
        };
    } catch (error) {
        console.error('❌ MarketTraders zone API test failed:', error);
        return {
            success: false,
            error,
        };
    }
};

// Test OrangLaut zone specifically  
export const testOrangLautZone = async () => {
    console.log('⛵ Testing OrangLaut zone (4.1) specifically...');
    return await testAllZoneEndpoints(); // Uses hardcoded ORANG_LAUT
};

// Test zone claiming workflow for any zone
export const testZoneClaimingWorkflowForZone = async (zoneId: string) => {
    console.log(`🔄 Testing zone claiming workflow for ${zoneId}...`);

    try {
        // Reset zone to available
        await timeCapsuleApi.updateZoneStatus(zoneId, {
            status: 'available',
            claimedBy: undefined,
        });

        // Wait a bit
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Claim the zone
        const claimedStatus = await timeCapsuleApi.claimZoneStatus(zoneId);
        console.log(`✅ Zone ${zoneId} claiming workflow completed:`, claimedStatus);

        return claimedStatus;
    } catch (error) {
        console.error(`❌ Zone ${zoneId} claiming workflow failed:`, error);
        throw error;
    }
};

// Reset all zones to available status
export const resetAllZones = async () => {
    console.log('🔄 Resetting all zones to available...');
    const allZoneIds = Object.values(ZONE_IDS);
    
    const results = await Promise.allSettled(
        allZoneIds.map(async (zoneId) => {
            try {
                const result = await timeCapsuleApi.updateZoneStatus(zoneId, {
                    status: 'available',
                    claimedBy: undefined,
                });
                console.log(`✅ Zone ${zoneId} reset to available`);
                return { zoneId, success: true, result };
            } catch (error) {
                console.error(`❌ Failed to reset zone ${zoneId}:`, error);
                return { zoneId, success: false, error };
            }
        })
    );

    const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
    const failed = results.length - successful;

    console.log(`🎯 Zone reset complete: ${successful} successful, ${failed} failed`);
    return results;
};

// Get status of all zones
export const getAllZoneStatuses = async () => {
    console.log('📊 Fetching all zone statuses...');
    const allZoneIds = Object.values(ZONE_IDS);
    
    const results = await Promise.allSettled(
        allZoneIds.map(async (zoneId) => {
            try {
                const status = await timeCapsuleApi.getZoneStatus(zoneId);
                return { zoneId, success: true, status };
            } catch (error) {
                console.error(`❌ Failed to get status for zone ${zoneId}:`, error);
                return { zoneId, success: false, error };
            }
        })
    );

    console.log('📊 All zone statuses fetched');
    return results;
};

// Discover which zones are actually available on the server
export const discoverAvailableZones = async () => {
    console.log('🔍 Discovering available zones...');
    const potentialZones = ['4.1', '4.2', '4.4', '5.0', '10.3', '15.7'];
    const availableZones: string[] = [];
    const unavailableZones: string[] = [];
    
    for (const zoneId of potentialZones) {
        try {
            console.log(`Testing zone ${zoneId}...`);
            await timeCapsuleApi.getZoneStatus(zoneId);
            availableZones.push(zoneId);
            console.log(`✅ Zone ${zoneId} is available`);
        } catch (error: any) {
            unavailableZones.push(zoneId);
            console.log(`❌ Zone ${zoneId} is not available:`, error.message);
        }
        
        // Small delay to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    console.log('📊 Discovery Results:');
    console.log('✅ Available zones:', availableZones);
    console.log('❌ Unavailable zones:', unavailableZones);
    
    return {
        availableZones,
        unavailableZones,
        totalTested: potentialZones.length,
    };
};

// Test only available zones (safer version of getAllZoneStatuses)
export const getAvailableZoneStatuses = async () => {
    console.log('📊 Fetching available zone statuses only...');
    const availableZoneIds = Object.values(ZONE_IDS); // Only includes confirmed zones now
    
    const results = await Promise.allSettled(
        availableZoneIds.map(async (zoneId) => {
            try {
                const status = await timeCapsuleApi.getZoneStatus(zoneId);
                return { zoneId, success: true, status };
            } catch (error) {
                console.error(`❌ Failed to get status for zone ${zoneId}:`, error);
                return { zoneId, success: false, error };
            }
        })
    );

    console.log('📊 Available zone statuses fetched');
    return results;
};

// Development helper: expose zone test functions to window for console testing
if (process.env.NODE_ENV === 'development') {
    (window as any).zoneTests = {
        testAllZoneEndpoints,
        testZoneClaimingWorkflow,
        testZoneClaimingWorkflowForZone,
        testMarketTradersZone,
        testOrangLautZone,
        resetAllZones,
        getAllZoneStatuses,
        discoverAvailableZones,
        getAvailableZoneStatuses,
        // Quick test for zone 4.1
        testZone41: async () => {
            console.log('🧪 Quick test for zone 4.1...');
            try {
                const response = await timeCapsuleApi.getZoneStatus('4.1');
                console.log('✅ Zone 4.1 response:', response);
                return response;
            } catch (error) {
                console.error('❌ Zone 4.1 error:', error);
                return error;
            }
        },
        // Test Redux integration for zone 4.1
        testReduxZone41: async () => {
            console.log('🔧 Testing Redux integration for zone 4.1...');
            try {
                // Get the store from window (if available)
                const store = (window as any).__REDUX_STORE__;
                if (store) {
                    console.log('📊 Current Redux zones state:', store.getState().zones);
                    
                    // Dispatch getZoneStatus action
                    const action = await store.dispatch(require('../store/slices/zonesSlice').getZoneStatus('4.1'));
                    console.log('🔄 Dispatch result:', action);
                    
                    // Check state after dispatch
                    console.log('📊 Redux state after dispatch:', store.getState().zones);
                    return action;
                } else {
                    console.warn('⚠️ Redux store not accessible from window');
                    return null;
                }
            } catch (error) {
                console.error('❌ Redux test error:', error);
                return error;
            }
        },
        // Quick test for zone 4.2
        testZone42: async () => {
            console.log('🧪 Quick test for zone 4.2...');
            try {
                const response = await timeCapsuleApi.getZoneStatus('4.2');
                console.log('✅ Zone 4.2 response:', response);
                return response;
            } catch (error) {
                console.error('❌ Zone 4.2 error:', error);
                return error;
            }
        },
        ZONE_IDS,
    };
    
    console.log('🛠️ Zone test utilities available at window.zoneTests');
    console.log('Available functions:', Object.keys((window as any).zoneTests));
    console.log('💡 Try: await window.zoneTests.testZone41()');
    console.log('💡 Try: await window.zoneTests.testZone42()');
    console.log('💡 Try: await window.zoneTests.testMarketTradersZone()');
    console.log('💡 Try: await window.zoneTests.discoverAvailableZones()');
} 